import { useNotification } from '@ctn/ui/components/ctn-notifications';
import CTNSteps from '@ctn/ui/components/ctn-steps';
import { type CTNStepsStepProps } from '@ctn/ui/types/ctn-steps';
import { useEffect, useMemo, useState } from 'react';
import { type FieldErrors, useFormContext } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import GroupButtons from '@/components/GroupButtons';
import PageHeader from '@/components/PageHeader';
import { useDirtyForm } from '@/context/dirtyForm.context';
import { useGlobalContext } from '@/context/global.context';
import { usePageContext } from '@/context/page.context';
import { useSolicitudContext } from '@/context/solicitud.context';
import useApiData from '@/hooks/useApiData';
import { useLoadDraft } from '@/hooks/useLoadDraft';
import useRouteTramite from '@/hooks/useRouteTramite';
import SolicitudDocumentacionSection from '@/pages/TramiteNuevo/Step1';
import IntervinientesSection from '@/pages/TramiteNuevo/Step2';
import ConfirmacionEnvioSection from '@/pages/TramiteNuevo/Step4/ConfirmacionEnvioSection';
import ConfirmacionEnvioSummary from '@/pages/TramiteNuevo/Step4/ConfirmacionEnvioSummary';
import { postEnviarBorrador, postGuardarBorrador } from '@/services/tramite.service';

import { ERROR_TYPE } from '@/constants/errors.constants';
import { ROUTES, ROUTES_LABEL } from '@/constants/routes.constants';
import { STEP_ID } from '@/constants/tramite.constants';
import { mapFormToDB } from '@/helpers/formSolicitudes';
import { getBreadcrumbTramite } from '@/helpers/navigation';
import { notificationsConfig } from '@/helpers/notifications';
import { flatErrors, peekValidation } from '@/helpers/validations';
import { solicitudSchema } from '@/helpers/validationsSolicitudes';
import { type FormValues } from '@/interfaces/model/don.interface';

import SeleccionDeNotario from './Step3';

type StepId = (typeof STEP_ID)[keyof typeof STEP_ID];

const STEP_LABEL = {
	DOCUMENTACION: 'Solicitud y documentación',
	SUJETOS: 'Intervinientes',
	SELECCION_NOTARARIO: 'Asignación de notario',
	CONFIRMACION: 'Confirmación y envío',
};

const STEPS_RENDER = {
	[STEP_ID.DOCUMENTACION]: { header: STEP_LABEL.DOCUMENTACION, body: <SolicitudDocumentacionSection /> },
	[STEP_ID.SUJETOS]: { header: STEP_LABEL.SUJETOS, body: <IntervinientesSection /> },
	[STEP_ID.SELECCION_NOTARARIO]: { header: STEP_LABEL.SELECCION_NOTARARIO, body: <SeleccionDeNotario /> },
	[STEP_ID.CONFIRMACION]: {
		header: STEP_LABEL.CONFIRMACION,
		summary: <ConfirmacionEnvioSummary isEditable title="Solicitud" />,
		body: <ConfirmacionEnvioSection />,
	},
};

const STEPS_VALIDATION: Record<StepId, Array<keyof FormValues> | undefined> = {
	[STEP_ID.DOCUMENTACION]: ['numeroTramite', 'fechaVencimiento', 'documentosAdicionales', 'documentosRequeridos'],
	[STEP_ID.SUJETOS]: ['sujetosRequeridos', 'sujetosAdicionales'],
	[STEP_ID.SELECCION_NOTARARIO]: ['notarioSeleccionado'],
	[STEP_ID.CONFIRMACION]: [],
};

const TramiteNuevo = () => {
	const { appendBreadcrumb, onBackClick, setUrlBack, scrollTop } = usePageContext();
	const navigate = useNavigate();
	const location = useLocation();
	const fromSolicitudes = location.state?.fromSolcitudes;
	const { id } = useParams();
	const { isLoading: loadingDraft, reload: reloadDraft } = useLoadDraft(Number(id));
	const { openNotification, closeNotification } = useNotification();
	const configuracionTemplate = notificationsConfig({ closeNotification });
	const { activeStep, setActiveStep, maxStep, setMaxStep, requerirDocumentacion, setRequerirDocumentacion } =
		useSolicitudContext();
	const { triggerFetch, loading } = useApiData({ loading: ['saveDraft', 'getDraft', 'sendDraft'] as const });
	const { isDirtyForm, setIsDirtyForm, checkDirtyForm } = useDirtyForm();
	const [actionFired, setActionFired] = useState<'SAVE' | 'NEXT' | 'SEND'>();
	const ROUTES_TRAMITE = useRouteTramite();
	const {
		watch,
		trigger,
		control,
		getValues,
		clearErrors,
		formState: { dirtyFields },
	} = useFormContext<FormValues>();

	const {
		user,
		getTramiteData,
		maestrosTramite: { tipoDocumento, tipoIntervencion, configuracionTramite },
	} = useGlobalContext();

	const politicaAceptada = watch('politicaAceptada')?.[0] === '1';

	const STEPS_NUEVA_OPERACION: CTNStepsStepProps[] = [
		{
			id: STEP_ID.DOCUMENTACION,
			title: STEP_LABEL.DOCUMENTACION,
			active: true,
		},
		{
			id: STEP_ID.SUJETOS,
			title: STEP_LABEL.SUJETOS,
			active: false,
		},
		{
			id: STEP_ID.CONFIRMACION,
			title: STEP_LABEL.CONFIRMACION,
			active: false,
		},
	];

	const STEPS_NUEVA_OPERACION_NOTARIO: CTNStepsStepProps[] = [
		{
			id: STEP_ID.DOCUMENTACION,
			title: STEP_LABEL.DOCUMENTACION,
			active: true,
		},
		{
			id: STEP_ID.SUJETOS,
			title: STEP_LABEL.SUJETOS,
			active: false,
		},
		{
			id: STEP_ID.SELECCION_NOTARARIO,
			title: STEP_LABEL.SELECCION_NOTARARIO,
			active: false,
		},
		{
			id: STEP_ID.CONFIRMACION,
			title: STEP_LABEL.CONFIRMACION,
			active: false,
		},
	];

	const getCorrectSteps = () =>
		configuracionTramite?.permitePreseleccionNotario ? STEPS_NUEVA_OPERACION_NOTARIO : STEPS_NUEVA_OPERACION;

	const renderStep = useMemo(() => STEPS_RENDER[activeStep], [activeStep]);

	/**
	 * Evalúa la validez de todos los pasos del formulario para determinar el step máximo accesible.
	 * 1. Ejecuta las validaciones de cada step.
	 * 2. Ordena los resultados secuencialmente por el id.
	 * 3. Calcula el índice del último paso válido consecutivo para establecer el límite de navegación (maxStep).
	 */
	const getAvailablesSteps = async () => {
		const currentValues = getValues();

		const validateAllSteps = Object.entries(STEPS_VALIDATION).map(async ([stepKey, fields]) => {
			const step = Number(stepKey);
			if (!fields) return { step, isValid: true };

			const isValid = await peekValidation({
				schema: solicitudSchema({
					tipoDocumentoMaestro: tipoDocumento,
					configuracionTramite,
					tipoIntervencionMaestro: tipoIntervencion,
					globalValues: currentValues,
				}),
				data: currentValues,
				fields,
			});

			return { step, isValid };
		});

		const isValidStep = await Promise.all(validateAllSteps);
		isValidStep.sort((a, b) => a.step - b.step);

		let currentMaxStep = -1;

		// Si es válido, avanzamos el máximo paso permitido
		for (const curr of isValidStep) {
			if (!curr.isValid) {
				if (currentMaxStep === -1) currentMaxStep++;
				break;
			}

			currentMaxStep++;
		}

		return currentMaxStep > -1 ? currentMaxStep : 0;
	};

	const getIsValidValue = async (bySteps?: Array<keyof typeof STEPS_VALIDATION>, isFromButton?: boolean): Promise<boolean> => {
		let isValid = false;
		if (bySteps === undefined) {
			isValid = await trigger();
		} else {
			const validations = bySteps.reduce<Array<keyof FormValues>>(
				(acc, curr) => [...acc, ...(STEPS_VALIDATION[curr] as Array<keyof FormValues>)],
				[],
			);

			const filteredValidations =
				requerirDocumentacion && !isFromButton
					? validations
					: validations.filter((v) => v !== 'documentosRequeridos');

			if (!requerirDocumentacion || isFromButton) {
				clearErrors('documentosRequeridos');
			}

			isValid = await trigger(filteredValidations);
		}

		return isValid;
	};

	const openAdviceNotification = (title: string, message: string): void => {
		openNotification(
			configuracionTemplate.adviceMessage(
				<>
					<p>
						<b>{title}</b>
					</p>
					<p className="u-mt-16">
						{message}
					</p>
				</>
			));
	};

	const handleExistsRequiredErrors = (errors: FieldErrors): boolean => {
		const errorRequired = Object.values(flatErrors(errors)).some(({ type }) => type === ERROR_TYPE.required);

		if (errorRequired) {
			openNotification(
				configuracionTemplate.erroresForm(
					'Es necesario informar todos los campos obligatorios marcados con *.',
				),
			);
			return true;
		}
		return false;
	};

	const handleExistsSpecificErrors = (errors: FieldErrors, isFromButton?: boolean): boolean => {
		const errorMinDocument = Object.values(flatErrors(errors)).some(
			({ type }) => type === ERROR_TYPE.documentoRequired,
		);
		if (requerirDocumentacion && errorMinDocument && !isFromButton) {
			openAdviceNotification('Documentación requerida no aportada', 'No podrás enviar la solicitud hasta rellenar toda la documentación requerida.');
			return true;
		}

		const errorMinSujeto = Object.values(flatErrors(errors)).some(({ type }) => type === ERROR_TYPE.sujetoRequired);
		if (errorMinSujeto) {
			openAdviceNotification('Interviniente requerido no completado', 'No podrás enviar la solicitud hasta rellenar todos los intervinientes requeridos.');
			return true;
		}

		const notario = getValues('notarioSeleccionado');
		if (!notario && activeStep >= 2) {
			openAdviceNotification('Notario no seleccionado', 'No podrás continuar con la solicitud hasta seleccionar notario.');
			return true;
		}

		const firmaTelematica = getValues('firmaTelematica');
		if (firmaTelematica === undefined && activeStep >= 2) {
			openAdviceNotification('No se ha seleccionado el tipo de firma.', 'Seleccione una de las dos opciones disponibles (presencial o videoconferencia) para poder continuar.');
			return true;
		}
		return false;
	};

	/**
	 * Valida el formulario de forma integral o por pasos específicos.
	 * Si no se define, valida todo el formulario.
	 * Si falla, dispara una notificación específica (campos obligatorios,
	 * documentación mínima o errores genéricos) y devuelve `false`.
	 */
	const validateForm = async (bySteps?: Array<keyof typeof STEPS_VALIDATION>, isFromButton?: boolean) => {
		const isValid = await getIsValidValue(bySteps, isFromButton);
		if (isValid) return true;

		const { errors } = control._formState;

		/** Errores que se quieren mostrar con un formato específico diferente de configuracionTemplate.erroresForm */
		if (handleExistsRequiredErrors(errors)) {
			return false;
		}

		if (handleExistsSpecificErrors(errors, isFromButton)) {
			return false;
		}

		// Resto de errores de formulario.
		const listaErrores = Object.values(errors).map(({ message }) => message as string);

		if (listaErrores && !isFromButton) {
			openNotification(
				configuracionTemplate.erroresForm(
					<ul>
						{listaErrores.map((error, index) => (
							<li key={`step-error-${index - 1}`}>{error}</li>
						))}
					</ul>,
				),
			);

			return false;
		}

		return true;
	};

	const guardarBorrador = async ({
		showSuccess = true,
		validationBySteps,
		isFromButton = false,
	}: {
		showSuccess?: boolean;
		validationBySteps?: number[];
		isFromButton: boolean;
	}) => {
		const isValid = await validateForm(validationBySteps, isFromButton);
		if (!isValid || !user) return false;

		const payload = mapFormToDB({ form: getValues(), user, id: id ? Number(id) : undefined });

		const hasErrorDocument = payload?.tramiteTMU?.documentos.some(
			// @ts-expect-error pass
			(documento) => documento.tipoDocumento?.vigente === false,
		);

		if (hasErrorDocument) {
			openNotification(
				configuracionTemplate.errorMessage('Se han encontrado documentos adicionales no vigentes.'),
			);
			return false;
		}

		const { data } = await triggerFetch({
			fetch: postGuardarBorrador,
			payload,
			name: 'saveDraft',
			error: 'Ha ocurrido un error en el guardado del borrador',
		});

		if (!data) return false;

		if (id) {
			reloadDraft();
		} else {
			navigate(`${ROUTES_TRAMITE.BORRADORES}/${Number(data)}`);
		}

		if (showSuccess) {
			openNotification(
				configuracionTemplate.successMessage('Guardar borrador', 'El borrador ha sido guardado con éxito.'),
			);
		}

		return true;
	};

	const getTargetStep = (stepId: number) => {
		const currentSteps = getCorrectSteps();
		const isStepInFlow = currentSteps.some((s) => s.id === stepId);

		let targetStepId = stepId;

		if (!isStepInFlow) {
			if (stepId > activeStep) {
				targetStepId = STEP_ID.CONFIRMACION;
			} else {
				targetStepId = STEP_ID.SUJETOS;
			}
		}

		return targetStepId;
	};

	const saveDirtyBorrador = async (targetStepId: number) => {
		const isValid = await guardarBorrador({
			showSuccess: false,
			validationBySteps: [activeStep],
			isFromButton: false,
		});

		if (isValid) {
			setActiveStep(targetStepId);
			if (maxStep < targetStepId) setMaxStep(targetStepId);
			setIsDirtyForm(false);
		}
	};

	const goNextStep = async (targetStepId: number) => {
		const isValid = await validateForm([activeStep], false);
		if (isValid) {
			setActiveStep(targetStepId);
			if (maxStep < targetStepId) setMaxStep(targetStepId);
		}
	};

	/**
	 *  Maneja el cambio de step:
	 * 1. Si hay cambios (isDirty): Se intenta guardar un borrador.
	 * - Si el guardado es exitoso (pasa validación del step actual), avanza.
	 *
	 * 2. Si NO hay cambios:
	 * - Hacia adelante (step > activeStep): Se requiere validación técnica.
	 * - Hacia atrás: No se valida, permitiendo libre navegación a secciones previas.
	 */
	const onChangeDirtyStep = async (stepId: number) => {
		const data = user && mapFormToDB({ form: getValues(), user, id: id ? Number(id) : undefined });
		const hasErrorDocument = data?.tramiteTMU?.documentos.some(
			// @ts-expect-error pass
			(documento) => documento.tipoDocumento?.vigente === false,
		);

		if (hasErrorDocument) {
			openNotification(
				configuracionTemplate.errorMessage('Se han encontrado documentos adicionales no vigentes.'),
			);
			return false;
		}

		const targetStepId = getTargetStep(stepId);

		if (isDirtyForm) {
			await saveDirtyBorrador(targetStepId);
			return;
		}

		if (targetStepId > activeStep) {
			await goNextStep(targetStepId);
			return;
		}

		setActiveStep(targetStepId);
		if (maxStep < targetStepId) setMaxStep(targetStepId);
	};

	const onSiguiente = async () => {
		setActionFired('NEXT');
		await onChangeDirtyStep(activeStep + 1);
		setActionFired(undefined);
	};

	const onEnviar = async () => {
		if (!id || !user) return;
		const numeroTramite = getValues('numeroTramite');
		setActionFired('SEND');
		const response = await postEnviarBorrador({
			idSolicitud: Number(id),
			nifEmisor: user?.nifEmisor,
			tipoSector: user?.tipoSectorEmisor,
		});
		setActionFired(undefined);

		if (response.code === 200) {
			navigate(ROUTES_TRAMITE.TRAMITE_SUCCESS, 
				{state: { numeroTramite, id: response.data }}
			);
			return;
		}

		if (response.code === 406 && response.data?.errores) {
			openNotification(
				configuracionTemplate.error406({
					message: response.data?.message,
					errores: response.data?.errores,
				}),
			);
			return;
		}

		openNotification(
			configuracionTemplate.errorMessage(
				<>
					<p>Se ha producido un error en el envió.</p>
					<p>Por favor, vuelva a intentarlo</p>
				</>,
			),
		);
	};

	const cancelar = () => {
		checkDirtyForm({
			action() {
				onBackClick();
			},
		});
	};

	// Si se encuentra algún campo dirty se activará la modal de cambios pendientes cuando salgamos de la ruta.
	useEffect(() => {
		const isDirty = Object.values(dirtyFields).some(Boolean);
		setIsDirtyForm(isDirty);
	}, [Object.values(dirtyFields)]);

	useEffect(() => {
		if (!loadingDraft) {
			getAvailablesSteps().then((maxStep) => {
				setMaxStep(maxStep);
			});
		}
	}, [loadingDraft]);

	useEffect(() => {
		scrollTop();
	}, [activeStep]);

	useEffect(() => {
		appendBreadcrumb(ROUTES_LABEL.SOLICITUD_NUEVA, [
			getBreadcrumbTramite(getTramiteData()),
			{ label: ROUTES_LABEL.SOLICITUDES, url: ROUTES_TRAMITE.BORRADORES },
			{ label: ROUTES_LABEL.SOLICITUD_NUEVA, url: location.pathname },
		]);
		if (fromSolicitudes) {
			setUrlBack({label: 'Volver a solicitudes', navigate() {
				navigate(ROUTES.SOLICITUDES);
			}});
		}	
	}, []);

	useEffect(() => {
		if (
			configuracionTramite?.validarDocumentacionRequerida !== undefined &&
			configuracionTramite?.validarDocumentacionRequerida !== null
		) {
			setRequerirDocumentacion(configuracionTramite?.validarDocumentacionRequerida);
		}
	}, [configuracionTramite]);

	return (
		<>
			<PageHeader hasHelp title="Nueva solicitud de escritura" className="u-mt-8" />
			<CTNSteps
				maxIndex={maxStep}
				className="[ c-pnp-steps ] [ u-mt-24@portrait ]"
				steps={getCorrectSteps()}
				activeIndex={activeStep}
				onClick={(e) => {
					onChangeDirtyStep(e.id);
				}}
			/>

			<section className="[ c-pnp-box c-pnp-box--border c-pnp-box--breakout ] [ u-flow-border ]">
				{renderStep.header && (
					<header className="c-pnp-box__header">
						<h3 className="c-pnp-heading-h3">
							{`${activeStep + (!configuracionTramite?.permitePreseleccionNotario && activeStep === 3 ? 0 : 1)}. ${renderStep.header}`}
						</h3>
					</header>
				)}
				{renderStep.summary && (
					<section className="c-pnp-form-group c-pnp-form-group--accent c-pnp-form-group--borderless c-pnp-form-group--borderless">
						{renderStep.summary}
					</section>
				)}
				<div className="c-pnp-box__body">{renderStep.body}</div>
				<footer className="c-pnp-box__footer">
					<GroupButtons
						left={[
							{
								id: 'save',
								disabled: loadingDraft || loading.saveDraft || actionFired === 'SEND',
								variant: 'outline',
								loading: actionFired === 'SAVE',
								mode: 'secondary',
								label: 'Guardar',
								// Validación de todos los steps hasta el actual
								onClick() {
									const ejecutarGuardado = async () => {
										setActionFired('SAVE');
										await guardarBorrador({
											validationBySteps: [activeStep],
											isFromButton: true,
										});
										setActionFired(undefined);
									};

									void ejecutarGuardado();
								},
							},
						]}
						right={[
							{
								id: 'enviar',
								disabled:
									loadingDraft ||
									loading.saveDraft ||
									actionFired === 'SEND' ||
									(activeStep === STEP_ID.CONFIRMACION &&
										configuracionTramite?.permitePreseleccionNotario &&
										!politicaAceptada),
								mode: 'primary',
								loading: actionFired === 'NEXT' || actionFired === 'SEND',
								label: activeStep === STEP_ID.CONFIRMACION ? 'Enviar' : 'Siguiente',
								onClick() {
									if (activeStep === STEP_ID.CONFIRMACION) {
										void onEnviar();
									} else {
										void onSiguiente();
									}
								},
							},
							{
								id: 'cancelar',
								disabled: loading.saveDraft || loadingDraft || actionFired === 'SEND',
								mode: 'tertiary',
								label: 'Cancelar',
								onClick: cancelar,
							},
						]}
					/>
				</footer>
			</section>
		</>
	);
};

export default TramiteNuevo;
